<template>
    <div v-if="attr" ref="wrap" class="mvt-wrap" :class="{ 'mvt-multi': isMulti, 'mvt-glued': isMulti && glued, 'mvt-hbar-on': hbarOn }"
        :style="isMulti && mvtMaxH ? { maxHeight: mvtMaxH + 'px' } : null">
        <!-- IE 2026 10 08 / ODOO-343741: класс mvt-hbar-on (строка выше) — превью в склейке с прокси-полосой .mvt-hbar ниже -->
        <template v-if="isMulti">
            <div v-for="(sa, i) in sectionAttrs" :key="sa.html_id + '_' + sa._mvtKey"
                class="mvt-section" :style="{ height: sectionHeights[i] + 'px' }">
                <AttrGrid :widget_id="widget_id" :initialAttr="sa"
                    :ref="el => { sectionRefs[i] = el }" />
            </div>
            <!-- IE 2026 10 08 / ODOO-343741: общая горизонтальная полоса склейки — у нижнего края окна превью (sticky), секции за ней (mvt_hbar*) -->
            <div v-if="hbarOn" ref="hbar" class="mvt-hbar" @scroll="mvt_hbarScroll"><div ref="hbarIn" class="mvt-hbar-in"></div></div>
        </template>
        <AttrGrid v-else :key="'ptg' + printTplGen" :widget_id="widget_id" :attr_id="attr_id"
            :initialAttr="plainAttr" :parentAttr="parentAttr" />
    </div>
</template>

<script>
// ---------------------------------------------------------------------------
// AttrGridGroup («мульти вертикальная таблица» печати, тип AttrGridGroup).
//
// Верхний уровень значения (напр. _doc.print) — строки-секции
// секция: {attr_type:'AttrGridGroup', title, attrs:[...]}; ширины — width строк-ячеек.
// Каждая секция рендерится СВОИМ AttrGrid (стопкой) со своими ширинами колонок.
//
// Два режима (кнопка в командной панели превью, метод print_mvtView_Click):
//  - СКЛЕЙКА (по умолчанию): заголовки колонок только у первой секции, секции
//    без зазоров, общая вертикальная прокрутка (обёртка) и общая горизонтальная
//    (синхронизация scrollPosition.x; полоса — у последней секции, а у ПРЕВЬЮ —
//    прокси .mvt-hbar у нижнего края окна, mvt_hbar*);   IE 2026 10 08 / ODOO-343741
//  - СЕКЦИИ: каждый грид со своей шапкой и прокруткой.
// Нумерация строк СКВОЗНАЯ в обоих режимах (attr.rowIndexOffset).
//
// Экспорт со склейкой: mvt_exportXlsx — 1С-подход (базовые колонки из
// объединения границ колонок всех секций + colSpan на разных уровнях),
// mvt_exportPdf — секции последовательно в один PdfDocument.
// Горизонтальный родственник — AttrTabGridLanes («мульти горизонтальная»).
//
// РЕДАКТОР ШАБЛОНА (isPrintTemplate, binding '_doc.printTemplate') — ЭФЕМЕРНАЯ
// проекция канона doc.printTemplate: генерится при открытии (printTpl_ensure),
// в БД не сохраняется (_doc исключён из send). Все правки визуальной таблицы
// write-back'ом уходят в канон (printTpl_writeBack, debounce 100мс — идиом
// сводных таблиц aggAll_*): дисплейный мусор (сгенерённое превью шапки
// табличной части в attrs, col_N-дубликаты контента fields, _meta строк)
// отбрасывается clean-проекцией и в запись не попадает.
// ---------------------------------------------------------------------------
import { useMainStore } from '@/stores/mainStore'
import * as wjcCore from '@mescius/wijmo'
import * as wjGrid from '@mescius/wijmo.grid'
import * as wjcXlsx from '@mescius/wijmo.xlsx'
import * as wjGridXlsx from '@mescius/wijmo.grid.xlsx'
import * as wjPdf from '@mescius/wijmo.pdf'
import * as wjGridPdf from '@mescius/wijmo.grid.pdf'
import { mvtXlsx_sectionAccs, mvtXlsx_attrWidth } from '@/utils/printXlsxWidths'   // IE 2026 10 07 / ODOO-343741
import { mvtHbar_geometry, mvtHbar_left } from '@/utils/printHbar'   // IE 2026 10 08 / ODOO-343741

// эвристики экспорта (настройка только правкой кода):
// wordWrap в xlsx для строк длиннее 40 символов (иначе Excel сам растянет
// текст по пустым соседям — консистентнее с превью)
const MVT_XLSX_AUTO_WRAP = false

export default {
    props: {
        widget_id: [String, Number],
        attr_id: String,
        initialAttr: { type: Object, default: null },
        parentAttr: { type: Object, default: null },
    },
    data: () => ({
        WIDGET: {},
        attr: null,
        parent_attr: null,
        sectionRefs: [],
        glued: true, // режим по умолчанию — склеенные секции
        // поколение эфемерной копии шаблона: пересборка (printTpl_ensure) —
        // ремоунт гридов секций, иначе они держат ОТВЯЗАННУЮ старую копию
        // (правки уходили в никуда после позднего ответа BE)
        printTplGen: 0,
        // потолок высоты обёртки: высота ближайшего скроллера минус смещение —
        // иначе стопка секций распирает вкладку и скроллится ВЕСЬ виджет
        // (контент наезжал на sticky-шапки)
        mvtMaxH: null,
    }),
    setup() {
        const store = useMainStore()
        return { store }
    },
    computed: {
        rows() {
            if (!this.WIDGET?.record || !this.attr?.binding) return []
            const v = this.store.get_value(this.WIDGET.record, this.attr.binding)
            return Array.isArray(v) ? v : []
        },
        sections() {
            const secs = this.rows.filter(r => r && r.attr_type === 'AttrGrid' && Array.isArray(r.attrs))
            // секциями считаем только «чистый» секционный шаблон: все верхние строки —
            // секции (смешанный/плоский рендерится одиночным гридом как раньше)
            return (secs.length && secs.length === this.rows.length) ? secs : []
        },
        isMulti() { return this.sections.length > 0 },
        // IE 2026 10 08 / ODOO-343741: прокси-полоса — только у превью в склейке (редактор шаблона и режим секций — как было)
        hbarOn() { return this.isMulti && this.glued && !this.attr?.isPrintTemplate },
        sectionAttrs() {
            const attr = this.attr
            if (!attr) return []
            let rowOffset = 0
            return this.sections.map((sec, i) => {
                // ЛОКАЛЬНАЯ копия ширин: авто-оценку ниже нельзя мутировать в
                // _cells копии — write-back унёс бы её в канон
                const widths = { ...(sec?._cells?._section?.widths || {}) }
                // редактор шаблона: колонки без сохранённых ширин — авто-оценка
                // по контенту ячеек (лейбл title||binding||source), как превью
                // авто-сайзит по значениям записи; чисто дисплейно
                if (attr.isPrintTemplate) {
                    for (const r of (sec.attrs || [])) {
                        if (!r || typeof r !== 'object') continue
                        const ov = r._cells || {}
                        const keys = new Set(
                            [...Object.keys(ov), ...Object.keys(r)].filter(k => /^col_\d+$/.test(k)))
                        for (const k of keys) {
                            if (sec?._cells?._section?.widths?.[k] != null) continue // сохранённая ширина главнее
                            const f0 = ov[k]?.fields?.[0] || {}
                            const span = Math.max(1, parseInt(ov[k]?.colspan) || 1)
                            let label = f0.title || f0.binding
                                || (f0.source != null ? String(f0.source) : '')
                            if (!label && typeof r[k] === 'string') label = r[k].trim()
                            if (!label) continue
                            const chip = f0.binding ? 3 : 0 // чип типа перед лейблом
                            // крупный шрифт ячейки (титулы) — шире буквы
                            const fpx = parseInt(f0.style?.['font-size']) || 0
                            const perChar = fpx >= 16 ? fpx * 0.62 : 7.2
                            const px = Math.min(360, Math.max(48, Math.round((label.length + chip) * perChar + 20)))
                            const per = Math.max(48, Math.round(px / span))
                            if (!widths[k] || widths[k] < per) widths[k] = per
                        }
                    }
                }
                const a = { ...attr }
                a.attr_type = 'AttrGrid' // узел теперь типа AttrGridGroup — секции остаются гридами
                a.component = 'AttrGrid'
                a.id = undefined                    // полоса не регистрируется в attrs_vueObj
                // флаг бейджа вкладки не наследуем: у секции нет parent_attr (краш
                // set_AttrTabPanel_countItems_afterTimeout), бейдж ведёт одиночный режим
                delete a['can-set_AttrTabPanel_countItems']
                a.html_id = `${attr.html_id || attr.name || 'mvt'}_sec${i + 1}`
                // грид секции показывает ТОЛЬКО свои строки
                a.binding = `${attr.binding}[${i}].attrs`
                // своё имя: viewParams (ширины колонок и пр.) у каждой секции свои
                a.name = `${attr.name || 'print'}_sec${i + 1}`
                a.noResize = true
                // командная панель: в склейке — только у первой секции; в режиме
                // секций у РЕДАКТОРА шаблона панель нужна каждой секции (операции
                // со строками своей секции), у превью — всегда одна
                if (i > 0 && (this.glued || !attr.isPrintTemplate)) a.hideCommandPanel = true
                // склейка: шапка колонок только у первой секции (номера строк остаются)
                a.headersVisibility = (this.glued && i > 0) ? 'Row' : 'All'
                // сквозная нумерация строк (formatItem rowHeaders)
                a.rowIndexOffset = rowOffset
                rowOffset += (sec.attrs || []).length
                a.attrs = (attr.attrs || []).map(c =>
                    widths[c.binding] != null ? { ...c, width: widths[c.binding] } : c)
                // ключ пересборки: состав строк/режим/поколение копии изменились →
                // ремоунт грида (гриды держат ссылки на массив копии — после
                // printTpl_ensure старые ссылки мертвы)
                a._mvtKey = `${i}_${(sec.attrs || []).length}_${this.glued ? 'g' : 's'}_g${this.printTplGen}`
                return a
            })
        },
        sectionHeights() {
            // высота секции по её строкам (превью нередактируемое): _row.height
            // или дефолтная строка; шапка колонок — только где она видима
            const rowH = 26, headH = 30
            return this.sections.map((sec, i) => {
                let h = (this.glued && i > 0 ? 0 : headH) + 10
                for (const r of (sec.attrs || [])) {
                    h += (r && r._cells && r._cells._row && r._cells._row.height) || rowH
                }
                return Math.min(h, 4000)
            })
        },
        plainAttr() {
            // одиночный fallback-грид — честная AttrGrid-копия (узел может быть
            // полноценного типа AttrGridGroup)
            const a = { ...(this.initialAttr || this.attr) }
            a.attr_type = 'AttrGrid'
            a.component = 'AttrGrid'
            return a
        },
        printTpl_canon() {
            // ссылка на канонический массив (doc.printCells, реактивная): смена
            // извне (мастер, reload записи) — пересборка эфемерной матрицы
            const docB = this.printTpl_docBinding()
            return docB && this.WIDGET?.record ? this.store.get_value(this.WIDGET.record, docB) : null
        },
    },
    watch: {
        rows: {
            immediate: true,
            handler(v, old) {
            // превью: Reload виджета сбрасывает _doc.printPreview (mainStore,
            // executeWidgetMethod 'reload') — при видимой вкладке печати сразу
            // перезапрашиваем свежую печать (смена подписи/шаблона)
            if (!String(this.attr?.binding || '').startsWith('_doc.printPreview')) return
            // дедупликация превью: ячейки несут только ссылку fields_key —
            // описание резолвится из таблицы record._doc.printPreview_fields
            // ленивым НЕПЕРЕЧИСЛИМЫМ геттером (в модель ничего не пишется)
            this.printRefs_resolve(v)
            if ((old?.length) && !(v?.length) && this.$refs.wrap?.offsetParent) {
                if (this._pvReloadAt && Date.now() - this._pvReloadAt < 4000) return
                this._pvReloadAt = Date.now()
                this.store.executeStoreMethod(this.WIDGET.vueObj, {
                    command: 'executeWidgetMethod', method: 'get_print', binding: '_doc.printPreview',
                })
            }
            },
        },
        printTpl_canon(canon) {
            if (this._printTpl_writingBack) return
            if (!this._printTpl_built) return // лень: построит показ вкладки
            if (canon !== this._printTpl_srcRef) this.printTpl_ensure()
        },
    },
    created() {
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        const WORKSPACE = this.store.WORKSPACES[this.WIDGET.workspace]
        this.PAGE = WORKSPACE.PAGES[this.WIDGET.page]
        this.resolve_attr()
        // редактор шаблона: эфемерная копия _doc.printTemplate строится ЛЕНИВО —
        // при первом ПОКАЗЕ вкладки Template (IntersectionObserver в mounted),
        // а не при открытии сегмента (просьба Игоря)
        // компонент склейки ПРЕВЬЮ доступен экспорту (mainStore exportGrid);
        // у настроек (doc.printTemplate) свой экземпляр — не перетираем
        if (String(this.attr?.binding || '').startsWith('_doc.print')) {
            this.WIDGET._mvtPrint = this
        }
        // реестр для кнопки режима (Widget.vue::print_mvtView_Click)
        if (this.attr?.binding) {
            (this.WIDGET._mvtObjs = this.WIDGET._mvtObjs || {})[this.attr.binding] = this
        }
        // сохранённый режим
        if (this.attr?.name) {
            this.glued = !this.store.get_value(this.WIDGET.record, `doc.viewParams.${this.attr.name}.mvtGluedOff`)
        }
    },
    mounted() {
        // сквозные стрелки: Down в конце секции -> следующая, Up в начале -> предыдущая
        this.$refs.wrap?.addEventListener('keydown', this.mvt_keydown, true)
        // редактор шаблона: ленивое построение/обновление при ПОКАЗЕ вкладки
        // (первый показ — построение; повторный после правок Cells — пересборка:
        // printCellsTab_afterWB инвалидирует _printTpl_built)
        if (this.printTpl_docBinding() && this.$refs.wrap && typeof IntersectionObserver !== 'undefined') {
            this._printTpl_io = new IntersectionObserver((entries) => {
                if (entries.some(en => en.isIntersecting) && !this._printTpl_built) {
                    this.printTpl_ensure()
                }
            })
            this._printTpl_io.observe(this.$refs.wrap)
        }
        // потолок высоты: пересчёт при ресайзе окна/скроллера (сплиттеры,
        // pop-out, смена вкладки)
        this._mvtBoundRO = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => { this.mvt_boundHeight(); this.mvt_hbarUpdate() }) : null   // IE 2026 10 08 / ODOO-343741: + ход прокси-полосы
        // сам wrap тоже под наблюдением: показ скрытой вкладки (display:none →
        // размер с 0) иначе не даёт ни updated, ни ресайза скроллера
        if (this._mvtBoundRO && this.$refs.wrap) this._mvtBoundRO.observe(this.$refs.wrap)
        window.addEventListener('resize', this.mvt_boundHeight)
        this.$nextTick(() => this.mvt_boundHeight())
    },
    beforeUpdate() {
        // ремоунт секций (смена режима/данных): чистим refs, иначе копятся
        // отмонтированные гриды (дублировали секции в экспорте)
        this.sectionRefs = []
    },
    updated() {
        this.mvt_bindScroll()
        this.mvt_sizeSections()
        this.mvt_boundHeight()
        // страховка ленивого построения: инвалидация после правок Cells +
        // видимый wrap (переход на вкладку) → пересборка (IO мог не стрельнуть)
        if (!this._printTpl_built && this.printTpl_docBinding()
                && this.$refs.wrap && this.$refs.wrap.offsetParent) {
            this.printTpl_ensure()
        }
    },
    beforeUnmount() {
        // незаписанный дебаунс write-back — слить в канон до размонтирования
        if (this._printTpl_wbT) this.printTpl_writeBack()
        this._printTpl_io?.disconnect()
        this._mvtBoundRO?.disconnect()
        window.removeEventListener('resize', this.mvt_boundHeight)
        this.$refs.wrap?.removeEventListener('keydown', this.mvt_keydown, true)
        if (this.WIDGET && this.WIDGET._mvtPrint === this) delete this.WIDGET._mvtPrint
        if (this.attr?.binding && this.WIDGET?._mvtObjs?.[this.attr.binding] === this) {
            delete this.WIDGET._mvtObjs[this.attr.binding]
        }
        this.store.attr_deregister(this)
    },
    methods: {
        // -- ссылки превью печати (fields_key) -------------------------------
        // Бэкенд шлёт УНИКАЛЬНЫЕ описания полей ячеек одной плоской таблицей
        // record._doc.<binding>_fields (для _doc.printPreview —
        // _doc.printPreview_fields); ячейки строк несут только ссылку
        // fields_key. Здесь на ячейку вешается ленивый НЕПЕРЕЧИСЛИМЫЙ
        // геттер fields: потребители (AttrGrid, печать) читают полное
        // описание, но в модели/JSON остаётся только ссылка — без дублей,
        // без служебных флагов.
        printRefs_resolve(rows) {
            const table = this.store.get_value(this.WIDGET?.record, `${this.attr?.binding}_fields`)
            if (!Array.isArray(table) || !table.length) return
            const attach = (ent) => {
                const d = Object.getOwnPropertyDescriptor(ent, 'fields')
                if (d && (d.get || d.value !== undefined)) return // уже есть геттер или свои fields (легаси)
                let cache = null
                Object.defineProperty(ent, 'fields', {
                    enumerable: false,
                    configurable: true,
                    get() {
                        if (!cache) {
                            const def = table[Number(ent.fields_key)]
                            cache = def && typeof def === 'object' ? [def] : []
                        }
                        return cache
                    },
                    set(v) { // редактор/легаси: свои fields перекрывают ссылку
                        Object.defineProperty(ent, 'fields', {
                            enumerable: true, configurable: true, writable: true, value: v })
                    },
                })
            }
            const walk = (rs) => {
                for (const r of rs || []) {
                    if (!r || typeof r !== 'object') continue
                    if (r.attr_type === 'AttrGrid' && Array.isArray(r.attrs)) { walk(r.attrs); continue }
                    const ov = r._cells
                    if (!ov) continue
                    for (const k in ov) {
                        const ent = ov[k]
                        if (ent && typeof ent === 'object' && ent.fields_key != null) attach(ent)
                    }
                }
            }
            walk(rows)
        },
        // потолок высоты обёртки = высота ближайшего overflow-скроллера минус
        // смещение обёртки в нём: вертикальная прокрутка секций остаётся ВНУТРИ
        // группы, внешний контейнер (widget-attrs) не распирает
        mvt_boundHeight() {
            if (!this.isMulti) { this.mvtMaxH = null; return }
            const el = this.$refs.wrap
            if (!el || !el.offsetParent) return // вкладка скрыта — не трогаем
            let sc = el.parentElement
            while (sc && sc !== document.body) {
                const cs = getComputedStyle(sc)
                if (/(auto|scroll)/.test(cs.overflowY) && sc.clientHeight > 0) break
                sc = sc.parentElement
            }
            if (!sc || sc === document.body) return
            if (this._mvtBoundRO && this._mvtBoundSc !== sc) {
                if (this._mvtBoundSc) this._mvtBoundRO.unobserve(this._mvtBoundSc)
                this._mvtBoundRO.observe(sc)
                this._mvtBoundSc = sc
            }
            const top = el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop
            const h = Math.max(220, Math.floor(sc.clientHeight - top) - 6)
            if (this.mvtMaxH === null || Math.abs(this.mvtMaxH - h) > 1) this.mvtMaxH = h
        },
        mvt_activeSection() {
            const refs = this.sectionRefs || []
            return refs.find(r => r?.flex?.selection?.isValid) || refs[0] || null
        },
        // делегация grid-методов АКТИВНОЙ секции: кнопки командной панели
        // (executeStoreMethod/executeVueObjMethod) резолвят vueObj по attr.id —
        // там зарегистрирован координатор, а не грид секции
        ...Object.fromEntries(['new_Click', 'delete_Click', 'up_Click', 'down_Click',
            'left_Click', 'right_Click', 'merge_Click', 'print_cellsList_Click',
            'undo_Click', 'redo_Click', 'onFillColor', 'onRowStyleColor', 'onRowStyleCustom',
            'onImporttGrid__Click', 'onExportGrid__Click', 'copy_Click', 'paste_Click',
            'clone_Click', // кнопка «клон» панели — метод грида (падала null на превью/шаблоне)
            'fullScreen_Click', 'onToggle_gridSettings', 'select_search',
        ].map(m => [m, function (...args) {
            const s = this.mvt_activeSection()
            return s && typeof s[m] === 'function' ? s[m](...args) : undefined
        }])),
        // структурные операции: номера строк сдвинулись — открытый 👁-список
        // ячеек пересобирается (иначе показывал старые Row-номера)
        ...Object.fromEntries(['new_Click', 'delete_Click', 'up_Click', 'down_Click', 'clone_Click'
        ].map(m => [m, function (...args) {
            const s = this.mvt_activeSection()
            if (!s || typeof s[m] !== 'function') return undefined
            const res = s[m](...args)
            this.printTpl_afterChange() // структура эфемерной таблицы изменилась → канон
            setTimeout(() => {
                const WRow = this.store.get_childrenWidget?.(this.WIDGET, 'widget-row')
                const nm = String(WRow?.record?._meta?.name || '')
                if (WRow && !WRow._softClosed
                        && WRow.record?._meta?.type === 'widget-row-viewParams'
                        && nm.endsWith('-cells')) {
                    s.print_cellsList_Click?.()
                }
            }, 600)
            return res
        }])),
        // --- эфемерный редактор шаблона печати (_doc.printTemplate) ---------
        // КАНОН (модель Игоря 2026-07-25) — ХРАНИМЫЙ doc.printCells (секции-гриды
        // со строками-ячейками; row локальный, width в строке-ячейке). Редактор
        // собирает матрицу секций в _doc.printTemplate (printCells_toMatrix),
        // правки уезжают обратно (printCells_fromMatrix -> doc.printCells).
        printTpl_docBinding() {
            // канонический биндинг write-back'а; не редактор — null
            const b = String(this.attr?.binding || '')
            return (this.attr?.isPrintTemplate && b.startsWith('_doc.')) ? 'doc.printCells' : null
        },
        // -- конвертеры канона (зеркала python print_cells_to_matrix и обратно) --
        printCells_normalize(cells) {
            const out = [], loose = []
            const flush = () => { if (loose.length) { out.push({ attr_type: 'AttrGrid', title: '', attrs: loose.splice(0) }) } }
            for (const n of cells || []) {
                if (this.store.isinstance_dict(n) && n.attr_type === 'AttrGrid'
                        && Array.isArray(n.attrs) && !n.binding) {
                    flush(); out.push(n)
                } else { loose.push(n) }
            }
            flush()
            return out
        },
        printCells_fieldOf(node) {
            const f = {}
            if (node.binding) f.binding = node.binding
            const sv = (node.source_field && typeof node.source_field === 'object') ? node.source_field.value : undefined
            if (sv !== undefined && sv !== null && !f.binding) f.source = sv
            if (node.title) f.title = node.title
            const at = node.attr_type
            if (at && !['AttrStr', 'Source', 'AttrGrid'].includes(at)) f.attr_type = at
            else if (at === 'AttrStr' && f.binding) f.attr_type = 'AttrStr'
            for (const k of ['format', 'relation_segment', 'relation_segment_binding', 'style']) {
                if (node[k]) f[k] = node[k]
            }
            return f
        },
        printCells_toMatrix(cells) {
            const sections = []
            for (const sec of this.printCells_normalize(cells)) {
                const nodes = (sec.attrs || []).filter(n => this.store.isinstance_dict(n))
                let maxrow = 0
                for (const n of nodes) maxrow = Math.max(maxrow, parseInt(n.row) || 0)
                const rows = Array.from({ length: maxrow }, () => ({}))
                const widths = {}
                const put = (row, n, isCol) => {
                    const ci = parseInt(n.col) || 0
                    if (ci < 1) return
                    const ov = row._cells = row._cells || {}
                    const ent = { fields: [this.printCells_fieldOf(n)] }
                    if (parseInt(n.colspan) > 1) ent.colspan = parseInt(n.colspan)
                    if (parseInt(n.rowspan) > 1) ent.rowspan = parseInt(n.rowspan)
                    ov['col_' + ci] = ent
                    if (n.width && widths['col_' + ci] == null) widths['col_' + ci] = parseInt(n.width)
                    if (!isCol && n.height) (ov._row = ov._row || {}).height = parseInt(n.height)
                }
                for (const n of nodes) {
                    const ri = parseInt(n.row) || 0
                    if (ri < 1 || ri > maxrow) continue
                    const row = rows[ri - 1]
                    if (n.attr_type === 'AttrGrid' && n.binding) {
                        row[n.repeat ? 'repeat' : 'tablePart'] = n.binding
                        if (n.fieldsMapping) row.fieldsMapping = n.fieldsMapping
                        for (const c of (n.attrs || [])) {
                            if (this.store.isinstance_dict(c)) put(row, c, true)
                        }
                        continue
                    }
                    put(row, n, false)
                }
                const sect = { attr_type: 'AttrGrid', title: sec.title || '', attrs: rows }
                if (Object.keys(widths).length) sect._cells = { _section: { widths } }
                sections.push(sect)
            }
            return sections
        },
        printCells_fromMatrix(tpl) {
            const colN = k => parseInt(String(k).slice(4)) || 0
            const isSec = (tpl || []).length && tpl.every(r => this.store.isinstance_dict(r) && r.attr_type === 'AttrGrid' && Array.isArray(r.attrs))
            const secs = isSec ? tpl : [{ attrs: tpl || [], title: '' }]
            const out = []
            const nodeOf = (f0, staticV, ri, ci, ent, widths, rh) => {
                const node = { row: ri, col: ci }
                if (f0.binding) node.binding = f0.binding
                if (f0.title) node.title = f0.title
                let sv = f0.source
                if ((sv === undefined || sv === null) && typeof staticV === 'string' && staticV.trim()) sv = staticV
                if (sv !== undefined && sv !== null && !f0.binding) {
                    node.source_field = { value: sv }
                }
                node.attr_type = f0.attr_type || 'AttrStr'
                for (const kk of ['format', 'relation_segment', 'relation_segment_binding', 'style']) {
                    if (f0[kk]) node[kk] = f0[kk]
                }
                if (ent?.colspan) node.colspan = ent.colspan
                if (ent?.rowspan) node.rowspan = ent.rowspan
                if (widths['col_' + ci]) node.width = widths['col_' + ci]
                if (rh) node.height = rh
                return node
            }
            for (const s of secs) {
                const widths = s?._cells?._section?.widths || {}
                const nodes = []
                ;(s.attrs || []).forEach((rrow, i) => {
                    if (!this.store.isinstance_dict(rrow)) return
                    const ov = rrow._cells || {}
                    const tp = rrow.tablePart || rrow.repeat
                    const rh = ov._row?.height
                    if (tp) {
                        const cols = Object.keys(ov).filter(k => k.startsWith('col_')).sort((a, b) => colN(a) - colN(b))
                            .map(k => nodeOf((ov[k]?.fields || [{}])[0] || {}, null, i + 1, colN(k), ov[k], widths, null))
                        const node = { attr_type: 'AttrGrid', title: this.t('Table part'), binding: tp, row: i + 1, attrs: cols }   // IE 2026 09 23 / ODOO-341407: i18n
                        if (rrow.repeat) node.repeat = true
                        if (rrow.fieldsMapping) node.fieldsMapping = rrow.fieldsMapping
                        nodes.push(node)
                        return
                    }
                    const keys = new Set(Object.keys(ov).filter(k => k.startsWith('col_')))
                    for (const k of Object.keys(rrow)) {
                        if (k.startsWith('col_') && typeof rrow[k] === 'string' && rrow[k].trim()) keys.add(k)
                    }
                    for (const k of [...keys].sort((a, b) => colN(a) - colN(b))) {
                        const ent = ov[k] || {}
                        nodes.push(nodeOf((ent.fields || [{}])[0] || {}, rrow[k], i + 1, colN(k), ent, widths, rh))
                    }
                })
                out.push({ attr_type: 'AttrGrid', title: s.title || '', attrs: nodes })
            }
            return out
        },
        printCells_canon() {
            // канон записи: doc.printCells; legacy doc.printTemplate — конверт на лету
            const rec = this.WIDGET?.record
            const cells = this.store.get_value(rec, 'doc.printCells')
            if (Array.isArray(cells) && cells.length) return this.printCells_normalize(cells)
            return []
        },
        printTpl_ensure(force = false) {
            // собрать/пересобрать визуальную матрицу _doc.printTemplate[i] из
            // ХРАНИМОГО канона doc.printCells (служебный _-путь modified не метит)
            const docB = this.printTpl_docBinding()
            const rec = this.WIDGET?.record
            if (!docB || !rec) return
            const canon = this.store.get_value(rec, docB)
            const have = this.store.get_value(rec, this.attr.binding)
            if (!force && this._printTpl_built && Array.isArray(have) && this._printTpl_srcRef === canon) return
            this._printTpl_srcRef = canon
            this._printTpl_built = true
            const matrix = this.printCells_toMatrix(this.printCells_canon())
            this.store.set_value(rec, this.attr.binding, matrix, null, null, null, false)
            // новое поколение копии → ремоунт гридов секций/одиночного (их
            // data держит ссылки на СТАРЫЙ массив — правки ушли бы в никуда)
            this.printTplGen++
        },
        printTpl_afterChange() {
            // любая правка визуальной таблицы → отложенный write-back в канон
            // (debounce 100мс — идиом aggAll_afterChange).
            // ГВАРД built: до ленивого построения копия — пустышка fallback-грида
            // ([]); write-back с неё ЗАТИРАЛ канон пустым
            if (!this.printTpl_docBinding() || !this._printTpl_built) return
            clearTimeout(this._printTpl_wbT)
            this._printTpl_wbT = setTimeout(() => this.printTpl_writeBack(), this.store.setting('fe.ux.aggWriteBackMs', 100))   // IE 2026 09 05 / ODOO-336330
        },
        printTpl_writeBack() {
            clearTimeout(this._printTpl_wbT)
            this._printTpl_wbT = null
            const docB = this.printTpl_docBinding()
            const rec = this.WIDGET?.record
            if (!docB || !rec || !this._printTpl_built) return
            const src = this.store.get_value(rec, this.attr.binding)
            if (!Array.isArray(src)) return
            this._printTpl_writingBack = true
            try {
                // канон: обратная проекция матрицы редактора в printCells;
                // set_value метит запись modified (кнопка Save). Legacy
                // doc.printTemplate после первого write-back вычищаем — канон
                // теперь ТОЛЬКО printCells
                this.store.set_value(rec, docB, this.printCells_fromMatrix(src))
                this._printTpl_srcRef = this.store.get_value(rec, docB)
            } finally {
                this._printTpl_writingBack = false
            }
            // соседняя вкладка Print cells (список ячеек канона) — пересобрать
            for (const v of Object.values(this.WIDGET.attrs_vueObj || {})) {
                if (v?.printCellsTab_isActive?.() && v !== this) {
                    v.content_Changed?.({})
                    break
                }
            }
        },
        printTpl_clean(rows) {
            // обратная проекция: строки шаблона без дисплейного мусора —
            // attrs не-секционных строк (сгенерённое превью шапки табличной
            // части, prepare_printTemplate), col_N-дубликаты контента fields[0]
            // (текст рисует formatItem), служебные _-ключи кроме _cells
            return (rows || []).map(row => {
                if (!this.store.isinstance_dict(row)) return this.store.deepCopy(row)
                const isSection = row.attr_type === 'AttrGridGroup'
                const out = {}
                for (const k of Object.keys(row)) {
                    if (k === 'attrs') {
                        if (isSection) out.attrs = this.printTpl_clean(row.attrs)
                        continue
                    }
                    if (k.startsWith('_') && k !== '_cells') continue
                    if (/^col_\d+$/.test(k)) {
                        // контент задан fields[0] (binding/source/title) — значение
                        // col_N дисплейное; fields[0] только со стилем — текст
                        // старой модели живёт в col_N, сохраняем
                        const f0 = row._cells?.[k]?.fields?.[0]
                        if (f0 && (f0.binding || f0.source != null || f0.title)) continue
                    }
                    out[k] = this.store.deepCopy(row[k])
                }
                return out
            })
        },
        resolve_attr() {
            if (this.initialAttr && (this.store.tabs_noId || this.attr_id == null)) {
                this.attr = this.initialAttr
                this.parent_attr = this.parentAttr
            } else {
                this.store.widget_attr_set(this, true)
            }
            // регистрация под id атрибута НЕЗАВИСИМО от пути резолва: кнопки
            // командной панели (executeVueObjMethod: print_mvtView_Click) и
            // экспорт ищут vueObj в attrs_vueObj по attr.id
            const key = this.attr?.id || this.attr_id
            if (key && this.WIDGET?.attrs_vueObj) this.WIDGET.attrs_vueObj[key] = this
        },
        content_Changed(command) {
            this.resolve_attr()
            // запись могла замениться (updateDoc): эфемерную копию пересобрать
            // из свежего канона (no-op, если канон тот же — guard по srcRef;
            // непостроенная (лень) — построит показ вкладки)
            if (this._printTpl_built) this.printTpl_ensure()
            // данные обновляет реактивность (computed rows/sections);
            // одиночному гриду сообщает штатный broadcast
        },
        get_flex() {
            return this.sectionRefs?.[0]?.flex || null
        },
        print_mvtView_Click() {
            // кнопка панели превью (executeVueObjMethod)
            this.mvt_toggleGlued()
        },
        printTpl_refresh_Click() {
            // кнопка 🔄 вкладки Template: принудительная пересборка эфемерной
            // копии из канона doc.printTemplate (просьба Игоря)
            this.printTpl_ensure(true)
        },
        apply_CHIPS() { /* broadcast-шим: фильтров у превью нет */ },
        flexRefresh() {
            for (const r of this.sectionRefs || []) r?.flexRefresh?.()
        },
        print_cellsList_Click() {
            // 👁 по ВСЕМ секциям: узлы-группы секций (§), внутри — ячейки со
            // СКВОЗНЫМИ Row (просьба Игоря: группировочные строки секций в списке)
            const refs = (this.sectionRefs || []).filter(r => r?.flex)
            if (!refs.length) return
            const secNodes = this.sections.map((sec, i) => ({
                title: `§ ${sec.title || this.t('Section {n}', { n: i + 1 })}`,   // IE 2026 09 23 / ODOO-341407: i18n
                attr_type: 'AttrGrid', // секция = грид (список ячеек)
                row: (refs[i]?.attr?.rowIndexOffset || 0) + 1,
                attrs: refs[i]?.cellsList_build?.() || [],
            }))
            const cellsAttr = {
                name: (this.attr.name || 'printTemplate') + '-cells',
                title: this.t('Print cells'),   // IE 2026 09 23 / ODOO-341407: i18n
                binding: this.attr.binding,
                attrs: secNodes,
            }
            this.store.executeStoreMethod(this.WIDGET.vueObj, {
                command: 'widget_open', widget: 'widget-row', type: 'widget-row-viewParams',
                source_attr_type: 'AttrGrid', '_activate': true,
                binding: this.attr.binding, source_name: cellsAttr.name,
                get_Parent: true, open_in_children_widget: true,
                widgetRow_params: { top_pct: 0.2, left_pct: 0.2, width_pct: 0.8, height_pct: 0.8 },
            }, cellsAttr)
        },
        mvt_toggleGlued() {
            this.glued = !this.glued
            if (this.attr?.name && this.WIDGET?.record) {
                // инвертированный флаг: заполнен только в режиме «секции» —
                // v-if кнопок использует проверенный паттерн !!get_value(...)
                this.store.set_value(this.WIDGET.record, `doc.viewParams.${this.attr.name}.mvtGluedOff`, this.glued ? null : true)
            }
        },
        mvt_keydown(e) {
            // переход между секциями стрелками: вниз с последней строки секции —
            // на первую строку следующей; вверх с первой — на последнюю предыдущей
            // (зеркало laneKeydown «мульти горизонтальной» AttrTabGridLanes)
            if (!this.isMulti) return
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
            if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return
            const refs = this.sectionRefs || []
            const i = refs.findIndex(r => r?.flex?.hostElement?.contains(e.target))
            if (i < 0) return
            const fx = refs[i].flex
            const sel = fx.selection
            if (!sel?.isValid) return
            let target = -1, rowT = 0
            if (e.key === 'ArrowDown' && sel.row >= fx.rows.length - 1 && i < refs.length - 1) {
                target = i + 1; rowT = 0
            } else if (e.key === 'ArrowUp' && sel.row <= 0 && i > 0) {
                target = i - 1; rowT = (refs[i - 1]?.flex?.rows.length || 1) - 1
            }
            if (target < 0) return
            const fT = refs[target]?.flex
            if (!fT || !fT.rows.length) return
            e.preventDefault()
            e.stopPropagation()
            const col = Math.min(Math.max(sel.col, 0), fT.columns.length - 1)
            fT.focus()
            fT.select(new wjGrid.CellRange(rowT, col), true)
            fT.hostElement.scrollIntoView({ block: 'nearest' })
        },
        mvt_sizeSections() {
            // бесшовная склейка: точная высота секции по факту рендера
            // (строки + видимая шапка колонок), без запасов
            if (!this.isMulti) return
            requestAnimationFrame(() => {
                (this.sectionRefs || []).forEach((r, i) => {
                    const fx = r?.flex
                    if (!fx?.hostElement?.isConnected) return
                    // шапка с номерами колонок: в Sectioned — у КАЖДОЙ секции,
                    // в склейке — только у первой (биндинг иногда не долетает
                    // после ремоунта — ставим программно)
                    const hv = (this.glued && i > 0) ? wjGrid.HeadersVisibility.Row : wjGrid.HeadersVisibility.All
                    if (fx.headersVisibility !== hv) fx.headersVisibility = hv
                    if (!fx.rows?.length) return // данные ещё не пришли — оценку не сжимаем
                    const secEl = fx.hostElement.closest('.mvt-section')
                    if (!secEl) return
                    const root = fx.hostElement.querySelector("div[wj-part='root']")
                    if (!root) return
                    // live-замер по НИЗУ панели ячеек: умеет и расти, и СЖИМАТЬСЯ
                    // (scrollHeight не бывает меньше clientHeight — рос без отката)
                    const cellsEl = root.querySelector("div[wj-part='cells']")
                    if (!cellsEl) return
                    const sbH = Math.max(0, root.offsetHeight - root.clientHeight)
                    const need = cellsEl.getBoundingClientRect().bottom - root.getBoundingClientRect().top + sbH + 2
                    const diff = Math.round(need - root.clientHeight)
                    if (Math.abs(diff) > 2) {
                        secEl.style.height = (secEl.offsetHeight + diff) + 'px'
                    }
                })
                this.mvt_hbarUpdate()   // IE 2026 10 08 / ODOO-343741: ширины секций могли измениться (данные, колонки, окно)
            })
        },
        // IE 2026 10 08 / ODOO-343741: прокси-полоса склейки превью (utils/printHbar.js). Ход = наибольший ход секций;
        // полоса прокси → секции (mvt_hbarScroll), секция → прокси (mvt_hbarFollow из mvt_bindScroll); размеры и показ —
        // прямо в DOM, синхронно (как высоты секций в mvt_sizeSections), чтобы ход прокси не отставал от секций
        mvt_hbarUpdate() {
            const bar = this.$refs.hbar, inner = this.$refs.hbarIn
            if (!bar || !inner) return
            const flexes = this.mvt_flexes()
            const geo = mvtHbar_geometry(flexes.map(fx => fx.hostElement.querySelector("div[wj-part='root']")))
            const w = `calc(100% + ${geo.maxScroll}px)`
            if (inner.style.width !== w) inner.style.width = w
            const disp = geo.show ? 'block' : 'none'
            if (bar.style.display !== disp) bar.style.display = disp
            if (!geo.show) return
            // прокси — по секциям; построенная позже секция (стоит на 0) — к общей позиции
            const left = mvtHbar_left(flexes.map(fx => fx.scrollPosition.x), geo.maxScroll)
            if (Math.abs(bar.scrollLeft - left) >= 1) bar.scrollLeft = left
            this.mvt_hbarPush(-left)
        },
        mvt_hbarPush(x) {
            // все секции на x (ход каждой wijmo прижмёт к её пределу); их scrollPositionChanged под _scrollSyncing не эхом
            this._scrollSyncing = true
            try {
                for (const fx of this.mvt_flexes()) {
                    if (fx.scrollPosition.x !== x) fx.scrollPosition = new wjcCore.Point(x, fx.scrollPosition.y)
                }
            } finally { this._scrollSyncing = false }
        },
        mvt_hbarScroll() {
            // перетаскивание / трекпад / Shift+колесо на самой полосе → секции
            const bar = this.$refs.hbar
            if (!bar || this._scrollSyncing) return
            this.mvt_hbarPush(-bar.scrollLeft)
        },
        mvt_hbarFollow(x) {
            // секцию прокрутили сами (трекпад, Shift+колесо, клавиатура) — полоса за ней
            const bar = this.$refs.hbar
            if (!bar || bar.style.display === 'none') return
            if (-x > bar.scrollWidth - bar.clientWidth + 1) this.mvt_hbarUpdate()   // ход полосы устарел (секция стала шире)
            if (Math.abs(bar.scrollLeft + x) >= 1) bar.scrollLeft = -x
        },
        mvt_bindScroll() {
            // общая горизонтальная прокрутка в режиме склейки: скролл любой секции
            // синхронизирует остальные (полоса видима только у последней — CSS;
            // у превью — прокси .mvt-hbar, mvt_hbarFollow)   IE 2026 10 08 / ODOO-343741
            if (!this.isMulti) return
            for (const ref of this.sectionRefs || []) {
                const fx = ref?.flex
                if (!fx || fx.__mvtScrollBound) continue
                fx.__mvtScrollBound = true
                // свежесмонтированный грид держит selection (0,0) — рыжая рамка
                // (marquee) светилась в ПЕРВОЙ строке КАЖДОЙ секции; снимаем и
                // включаем marquee классом только у секции с реальным выделением
                fx.select(new wjGrid.CellRange(-1, -1), false)
                wjcCore.removeClass(fx.hostElement, 'mvt-sel-on')
                // выделение видно только в ОДНОЙ секции: выбор в одной чистит остальные
                fx.selectionChanged.addHandler(() => {
                    wjcCore.toggleClass(fx.hostElement, 'mvt-sel-on',
                        !!(fx.selection?.isValid && fx.selection.row >= 0 && fx.selection.col >= 0))
                    if (this._selSyncing || !fx.selection?.isValid) return
                    this._selSyncing = true
                    try {
                        for (const r2 of this.sectionRefs || []) {
                            const f2 = r2?.flex
                            if (f2 && f2 !== fx) {
                                if (f2.selection?.isValid) f2.select(new wjGrid.CellRange(-1, -1), false)
                                wjcCore.removeClass(f2.hostElement, 'mvt-sel-on')
                            }
                        }
                    } finally { this._selSyncing = false }
                })
                fx.updatedView.addHandler(() => { this.mvt_sizeSections() })
                fx.scrollPositionChanged.addHandler(() => {
                    if (this._scrollSyncing) return
                    // секции НЕ скроллятся вертикально (высота = контент): клавиатурная
                    // навигация не должна сдвигать контент секции (строки «уезжали»)
                    if (fx.scrollPosition.y !== 0) {
                        this._scrollSyncing = true
                        try { fx.scrollPosition = new wjcCore.Point(fx.scrollPosition.x, 0) }
                        finally { this._scrollSyncing = false }
                    }
                    if (!this.glued) return
                    this._scrollSyncing = true
                    try {
                        const x = fx.scrollPosition.x
                        for (const r2 of this.sectionRefs || []) {
                            const f2 = r2?.flex
                            if (f2 && f2 !== fx && f2.scrollPosition.x !== x) {
                                f2.scrollPosition = new wjcCore.Point(x, f2.scrollPosition.y)
                            }
                        }
                    } finally {
                        this._scrollSyncing = false
                    }
                    this.mvt_hbarFollow(fx.scrollPosition.x)   // IE 2026 10 08 / ODOO-343741: прокси-полоса превью — за секцией
                })
            }
        },
        mvt_flexes() {
            return (this.sectionRefs || []).map(r => r?.flex)
                .filter(fx => fx && fx.hostElement && fx.hostElement.isConnected)
        },
        mvt_debugBooks() {
            // ВРЕМЕННО: дамп книг секций (диагностика склейки xlsx)
            const flexes = this.mvt_flexes()
            return flexes.map((fx, i) => {
                const b = wjGridXlsx.FlexGridXlsxConverter.save(fx, {
                    includeColumnHeaders: false, includeRowHeaders: false,
                    formatItem: this.sectionRefs[i]?.formatItemXlsx,
                })
                return (b.sheets[0].rows || []).map(r => (r?.cells || []).map(c => ({
                    v: c?.value, s: c?.colSpan })).slice(0, 6))
            })
        },
        mvt_usedCols(sec) {
            // последняя колонка секции с контентом (матрица несёт полный набор
            // col_N — хвост пустых колонок в экспорт не тянем)
            let n = 1
            for (const r of (sec?.attrs || [])) {
                for (const k of Object.keys(r || {})) {
                    if (!k.startsWith('col_')) continue
                    const v = r[k]
                    const has = typeof v === 'string' ? !!v.trim() : v != null
                    const ov = r._cells?.[k]
                    if (has || (ov && ov.fields && ov.fields.length)) {
                        const c = parseInt(k.slice(4)) || 1
                        // мерж (colspan) тянет used-границу: скрытие колонок под
                        // мержем в PDF резало текст титула
                        const span = Math.max(1, parseInt(ov?.colspan) || 1)
                        n = Math.max(n, c + span - 1)
                    }
                }
            }
            return n
        },
        mvt_dataColWidth(i, binding) {
            // IE 2026 10 07 / ODOO-343741: ширина колонки НЕОТРИСОВАННОЙ секции — тем же порядком, что сборка
            // колонок AttrGrid: сохранённая ширина вида секции → явная ширина колонки → автоширина по данным секции
            const sa = this.sectionAttrs[i]
            if (!sa) return null
            const find = (list) => {
                for (const a of list || []) {
                    if (a?.binding === binding) return a
                    const sub = Array.isArray(a?.attrs) ? find(a.attrs) : null
                    if (sub) return sub
                }
                return null
            }
            const ac = find(sa.attrs)
            if (!ac) return null
            try {
                const vp = this.store.get_viewParam(this.WIDGET, sa.name, 'attr_by_binding', {}, sa)?.[binding]?.width
                if (vp) return vp
            } catch (e) { /* нет вида секции */ }
            const w = mvtXlsx_attrWidth(ac.width)
            if (w !== 'auto') return w
            const ref = this.sectionRefs?.[i]
            const auto = ref?.$options?.methods?.autoSizeColumn
            if (typeof auto !== 'function') return null
            try {
                // грид секции не построен — его data может быть ещё пуст: меряем по строкам секции
                const rows = Array.isArray(ref.data) && ref.data.length ? ref.data : (this.sections[i]?.attrs || [])
                const self = Object.create(ref, { data: { value: rows } })
                return auto.call(self, ac, 0, null) || null
            } catch (e) {
                return null
            }
        },
        mvt_exportXlsx(fileName) {
            // склейка секций в один лист НАПРЯМУЮ из данных (_doc.print):
            // грид-конвертер терял титул/числа. 1С-подход: базовые колонки =
            // объединение границ колонок секций, ячейка занимает свой интервал
            // (colSpan); длинный текст шапки/подвала — wrap
            const secs = this.sections
            if (!secs.length) return
            // используемые колонки секции — по ДАННЫМ
            const used = secs.map(sec => this.mvt_usedCols(sec))
            // границы: ширины колонок секций — px из грида секции (по ИНДЕКСУ секции);  IE 2026 10 07 / ODOO-343741
            // неотрисованная секция — ширина из её данных, как посчитал бы грид (было: 80 px и сдвиг индексов)
            const gridW = (this.sectionRefs || []).slice(0, secs.length).map(r => {   // IE 2026 10 07 / ODOO-343741
                const fx = r?.flex   // IE 2026 10 07 / ODOO-343741
                if (!fx || !fx.hostElement || !fx.hostElement.isConnected) return null   // IE 2026 10 07 / ODOO-343741
                const m = {}   // IE 2026 10 07 / ODOO-343741
                for (const col of fx.columns) if (col.binding && m[col.binding] == null) m[col.binding] = col.renderWidth || col.width || 0   // IE 2026 10 07 / ODOO-343741
                return m   // IE 2026 10 07 / ODOO-343741
            })
            const secAcc = mvtXlsx_sectionAccs(used, gridW, (i, b) => this.mvt_dataColWidth(i, b))   // IE 2026 10 07 / ODOO-343741
            const bounds = new Set()
            for (const acc of secAcc) for (const b of acc) bounds.add(b)
            const base = [...bounds].sort((a, b) => a - b)
            const kOf = {}
            base.forEach((b, k) => { kOf[b] = k })

            const book = new wjcXlsx.Workbook()
            const sheet = new wjcXlsx.WorkSheet()
            sheet.showGridlines = false
            book.sheets.push(sheet)
            for (let k = 1; k < base.length; k++) sheet.columns.push({ width: base[k] - base[k - 1] })

            const cssToStyle = (st, v) => {
                const out = {}
                if (st['font-weight'] === 'bold') (out.font = out.font || {}).bold = true
                const fs = parseInt(st['font-size'])
                if (fs) (out.font = out.font || {}).size = fs
                if (st['background-color']) out.fill = { color: st['background-color'] }
                if (MVT_XLSX_AUTO_WRAP && typeof v === 'string' && v.length > 40) out.wordWrap = true
                return Object.keys(out).length ? out : null
            }
            secs.forEach((sec, i) => {
                const acc = secAcc[i]
                for (const r of (sec.attrs || [])) {
                    // разреженный массив: ячейка по ИНДЕКСУ базовой колонки
                    // (пустышки {} обрывали сериализацию — писалась 1-я ячейка)
                    const cellsOut = []
                    for (let c = 1; c <= used[i]; c++) {
                        const key = 'col_' + c
                        const ov = r._cells?.[key]
                        const f0 = ov?.fields?.[0]
                        let v = r[key]
                        if (v === undefined || v === null) v = ''
                        const gridSpan = Math.max(1, ov?.colspan || 1)
                        const cEnd = Math.min(used[i] + 1, c + gridSpan)
                        const kStart = kOf[acc[c - 1]] ?? 0
                        const kEnd = Math.max(kStart + 1, kOf[acc[cEnd - 1]] ?? (kStart + 1))
                        const cell = { value: v }
                        const st = cssToStyle(f0?.style || {}, v)
                        if (st) cell.style = st
                        const span = kEnd - kStart
                        if (span > 1) cell.colSpan = span
                        cellsOut[kStart] = cell
                        c = cEnd - 1
                    }
                    const rh = r._cells?._row?.height
                    sheet.rows.push(rh ? { height: rh, cells: cellsOut } : { cells: cellsOut })
                }
            })
            book.saveAsync(`${fileName}.xlsx`)
        },
        mvt_exportPdf(fileName) {
            // секции последовательно в один PDF-документ; пустые/несмонтированные
            // гриды пропускаем (конвертер падает на них внутри — isVisible)
            const secs = this.sections
            const pairs = (this.sectionRefs || [])
                .map((r, i) => ({ fx: r?.flex, sec: secs[i] }))
                .filter(p => p.fx && p.fx.hostElement?.isConnected && p.fx.rows.length && p.fx.columns.length)
            if (!pairs.length) {
                this.store.show_notification({ text: this.t('Nothing to export'), status: 'warning' })   // IE 2026 09 23 / ODOO-341407: i18n
                return
            }
            // used-колонки и фактическая ширина секций — до создания документа:
            // очень широкое тело печатаем в АЛЬБОМНОЙ ориентации
            let maxW = 0
            for (const p of pairs) {
                p.usedN = this.mvt_usedCols(p.sec)
                let w = 0
                for (const col of p.fx.columns) {
                    const m = /^col_(\d+)$/.exec(col.binding || '')
                    if ((!m || +m[1] <= p.usedN) && col.visible) w += (col.renderWidth || col.width || 80)
                }
                p.usedW = w
                maxW = Math.max(maxW, w)
            }
            const landscape = maxW * 0.75 > 540 * 1.3
            const doc = new wjPdf.PdfDocument({
                ended: (s, args) => wjPdf.saveBlob(args.blob, `${fileName}.pdf`),
                pageSettings: { layout: landscape ? wjPdf.PdfPageOrientation.Landscape : wjPdf.PdfPageOrientation.Portrait },
            })
            // кириллица: регистрируем шрифт в документе (стандартные PDF-шрифты
            // её не содержат — в PDF была каша)
            try { doc.registerFont({ source: '/fonts/DejaVuSans.ttf', name: 'dejavu' }) } catch (e) { console.warn('pdf font:', e) }
            const optsBase = {
                maxPages: 1000,
                exportMode: wjGridPdf.ExportMode.All,
                // кириллица: встраиваем шрифт (стандартные PDF-шрифты её не содержат)
                embeddedFonts: [{ source: '/fonts/DejaVuSans.ttf', name: 'dejavu' }],
                styles: {
                    cellStyle: { font: { family: 'dejavu' }, backgroundColor: '#ffffff', borderColor: '#c6c6c6' },
                },
            }
            const pageW = doc.width || 540 // внутренняя ширина листа, pt
            for (const { fx, usedN, usedW } of pairs) {
                // матрица несёт полный набор col_N: пустой хвост прятал контент
                // в нечитаемый масштаб (или резал секцию на 3-4 страницы) —
                // скрываем его на время draw; шапки грида (номера строк/колонок)
                // в печатную форму не выводим
                const hidden = []
                for (const col of fx.columns) {
                    const m = /^col_(\d+)$/.exec(col.binding || '')
                    if (m && +m[1] > usedN && col.visible) { col.visible = false; hidden.push(col) }
                }
                const hv = fx.headersVisibility
                fx.headersVisibility = wjGrid.HeadersVisibility.None
                // шире листа — вписываем (иначе горизонтальная нарезка страниц);
                // уже — реальный размер (PageWidth раздул бы узкую шапку)
                const scaleMode = (usedW * 0.75 > pageW)
                    ? wjGridPdf.ScaleMode.PageWidth : wjGridPdf.ScaleMode.ActualSize
                try {
                    wjGridPdf.FlexGridPdfConverter.draw(fx, doc, null, null, { ...optsBase, scaleMode })
                } finally {
                    hidden.forEach(c => { c.visible = true })
                    fx.headersVisibility = hv
                }
                if (typeof doc.moveDown === 'function') doc.moveDown(1)
            }
            doc.end()
        },
    },
}
</script>

<style>
.mvt-wrap { display: flex; flex-direction: column; min-height: 0; overflow: auto; flex: 1 1 auto; }
.mvt-wrap.mvt-multi { gap: 2px; padding-bottom: 8px; }
.mvt-wrap.mvt-multi.mvt-glued { gap: 0; }
.mvt-section { flex: 0 0 auto; display: flex; flex-direction: column; }
.mvt-section .WIDGET-attr, .mvt-section .attr-grid { height: 100%; }
/* рамка активной ячейки (marquee) — только у секции с реальным выделением
   (класс mvt-sel-on ставит mvt_bindScroll): дефолтное selection (0,0) свежих
   гридов рисовало рамку в первой строке КАЖДОЙ секции */
.mvt-multi .mvt-section .wj-flexgrid .wj-marquee { visibility: hidden; }
.mvt-multi .mvt-section .wj-flexgrid.mvt-sel-on .wj-marquee { visibility: visible; }
/* склейка: горизонтальная полоса прокрутки — только у последней секции,
   остальные секции скроллятся синхронно (mvt_bindScroll) */
.mvt-glued .mvt-section:not(:last-child) .wj-flexgrid div[wj-part='root'] { overflow-x: hidden !important; }
/* IE 2026 10 08 / ODOO-343741: превью в склейке (mvt-hbar-on) — горизонтальная полоса ОДНА: прокси .mvt-hbar прилипает
   к нижнему краю окна превью (sticky) и видна без вертикальной прокрутки; своя полоса последней секции скрыта, но секция
   прокручивается трекпадом и Shift+колесом, как раньше (overflow-x не меняется); в печати (@media print) прокси нет */
.mvt-hbar-on .mvt-section .wj-flexgrid div[wj-part='root'] { scrollbar-width: none; }
.mvt-hbar-on .mvt-section .wj-flexgrid div[wj-part='root']::-webkit-scrollbar { display: none; }
/* прокси — последний ребёнок обёртки, последняя секция стала предпоследней: правило :not(:last-child) выше её не касается */
.mvt-hbar-on .mvt-section:nth-last-child(2) .wj-flexgrid div[wj-part='root'] { overflow-x: auto !important; }
/* bottom: −8px = padding-bottom обёртки mvt-multi: sticky считается от края без отступа — полоса у самого края окна */
.mvt-hbar { display: none; position: sticky; bottom: -8px; z-index: 6; flex: 0 0 auto; overflow-x: auto; overflow-y: hidden; background: #fff; }
.mvt-hbar-in { height: 1px; }
@media print { .mvt-hbar { display: none !important; } }
/* вертикально секции НЕ скроллятся сами (высота = контент, скроллит обёртка) —
   внутренние вертикальные полосы прокрутки убраны */
.mvt-multi .mvt-section .wj-flexgrid div[wj-part='root'] { overflow-y: hidden !important; }
.mvt-glued .mvt-section:not(:first-child) .wj-flexgrid { border-top: none; }
.mvt-glued .mvt-section:not(:last-child) .wj-flexgrid { border-bottom: none; }
.mvt-glued .mvt-section .wj-flexgrid { border-radius: 0; margin: 0; }
</style>
